/**
 * MobileDrillPlanWizard — two-step mobile-first drill plan builder.
 *
 * Step 1 — Drill Selection
 *   • Template cards (Positional Intro / Small Plan / Full)
 *   • Auto-filled slots driven by client posture + SoP + worst measurements
 *   • Tap any slot to swap the drill via a bottom-sheet picker
 *   • Dual suggestion toggle (posture-based vs. worst-measurement)
 *
 * Step 2 — Plan Details
 *   • Plan name input
 *   • Day tabs
 *   • Slots with inline Sets / Reps inputs — drill carousel fully hidden
 *   • Sticky "Save & Assign" footer
 *
 * Design principles:
 *   • Single-column, full-width
 *   • Minimum 44 px tap targets
 *   • No split views, no filter panels, no export/import/clone buttons
 */

import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  ChevronRight,
  FileDown,
  Loader2,
  Save,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/context/client-context';
import type { Drill } from '@/components/drill-library/types/drill-types';
import {
  POSITIONAL_INTRO_TEMPLATE,
  SMALL_DRILL_PLAN_TEMPLATE,
  FULL_TEMPLATE,
  type DrillPlanTemplateDefinition,
  type DrillPlanSlotMeta,
} from './lib/program-structure';
import type { DrillPlanDay, DrillPlanSlot } from './types/drill-plan-types';
import { buildDrillPlanTags } from './types/drill-plan-types';
import { useAutoFill, type SlotSuggestions } from './hooks/use-auto-fill';
import type { DrillSuggestionFilters } from '@/lib/posture-analysis/drill-suggestion-filters';
import { deriveClientPain, clientDisplayLabel } from '@/lib/clients/roster-fields';
import { createDrillPlan, createDrillPlanAssignment } from './services/drill-plans-api';
import { printDrillPlan } from '@/utils/programExport';
import logger from '@/lib/logging';
import ClientRequiredGate from './ClientRequiredGate';
import DrillLibraryPickerSheet from './DrillLibraryPickerSheet';

interface MobileDrillPlanWizardProps {
  drills: Drill[];
  onSaved?: (planId: string) => void;
  initialFilters?: DrillSuggestionFilters;
}

type WizardStep = 'template' | 'drills' | 'details';
type SuggestionMode = 'posture' | 'ai';

// ─── Template card ────────────────────────────────────────────────────────────

const TEMPLATE_DEFS = [
  {
    template: POSITIONAL_INTRO_TEMPLATE,
    colour: 'bg-status-clear-surface border-status-clear/30 text-status-clear',
    activeColour: 'bg-status-clear-surface border-status-clear',
  },
  {
    template: SMALL_DRILL_PLAN_TEMPLATE,
    colour: 'bg-status-info-surface border-status-info/30 text-status-info',
    activeColour: 'bg-status-info-surface border-status-info',
  },
  {
    template: FULL_TEMPLATE,
    colour: 'bg-accent/10 border-accent/30 text-accent',
    activeColour: 'bg-accent/20 border-accent',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeSlot(drillId: string): DrillPlanSlot {
  return { drillId, sets: '', reps: '', rest: '', tempo: '', notes: '' };
}

// ─── Main wizard ─────────────────────────────────────────────────────────────

const MobileDrillPlanWizard: React.FC<MobileDrillPlanWizardProps> = ({
  drills,
  onSaved,
  initialFilters,
}) => {
  const { user } = useAuth();
  const { client } = useClient();
  const { toast } = useToast();
  const { autoFill, isLoading: isAutoFilling } = useAutoFill();

  const [step, setStep] = useState<WizardStep>('template');
  const [template, setTemplate] = useState<DrillPlanTemplateDefinition | null>(null);
  const [planName, setPlanName] = useState('');
  const [days, setDays] = useState<DrillPlanDay[]>([]);
  const [filters, setFilters] = useState<DrillSuggestionFilters | null>(initialFilters ?? null);
  const [missedSlots, setMissedSlots] = useState<string[]>([]);
  const [slotSuggestions, setSlotSuggestions] = useState<Record<string, SlotSuggestions>>({});
  const [suggestionMode, setSuggestionMode] = useState<SuggestionMode>('posture');
  const [isSaving, setIsSaving] = useState(false);

  // Step 2 day tab
  const [activeDay, setActiveDay] = useState(0);

  // Drill Library picker sheet
  const [pickerSlot, setPickerSlot] = useState<{ meta: DrillPlanSlotMeta; dayIdx: number } | null>(null);

  const clientPhase = useMemo((): 'P1' | 'P2' | 'P3' | null => {
    if (!client?.classification) return null;
    const m = client.classification.match(/P([123])/i);
    return m ? (`P${m[1]}` as 'P1' | 'P2' | 'P3') : null;
  }, [client]);

  const drillById = useMemo(() => {
    const map: Record<string, Drill> = {};
    for (const d of drills) map[d.id] = d;
    return map;
  }, [drills]);

  const hasDualSuggestions = useMemo(
    () =>
      Object.values(slotSuggestions).some(
        (s) => s.postureBased && s.measurementBased && s.postureBased.id !== s.measurementBased.id,
      ),
    [slotSuggestions],
  );


  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSelectTemplate = async (tpl: DrillPlanTemplateDefinition) => {
    if (!client) return;
    setTemplate(tpl);
    setDays([]);
    setSlotSuggestions({});
    if (!planName) setPlanName(`${clientDisplayLabel(client)} – ${tpl.name} Plan`);

    try {
      const result = await autoFill(tpl, client, drills);
      setDays(result.days);
      setFilters(result.filters);
      setMissedSlots(result.missedSlots);
      setSlotSuggestions(result.slotSuggestions);
      setStep('drills');
      if (result.missedSlots.length > 0) {
        toast({ title: `${result.missedSlots.length} slot(s) need manual selection` });
      }
    } catch (err) {
      logger.error('drill-plan:auto-fill:error', err);
      toast({ title: 'Auto-fill failed — you can fill slots manually.', variant: 'destructive' });
      setStep('drills');
    }
  };

  const applyMode = (mode: SuggestionMode) => {
    setSuggestionMode(mode);
    if (!template || Object.keys(slotSuggestions).length === 0) return;
    setDays((prev) =>
      prev.map((day) => {
        const slots = { ...day.slots };
        for (const s of template.slots) {
          const sugg = slotSuggestions[s.id];
          if (!sugg) continue;
          const drill = mode === 'ai' ? sugg.measurementBased : sugg.postureBased;
          if (drill) slots[s.id] = makeSlot(drill.id);
        }
        return { ...day, slots };
      }),
    );
  };

  const handleSlotDrillSelect = (drillId: string) => {
    if (!pickerSlot) return;
    setDays((prev) =>
      prev.map((day, idx) => {
        if (idx !== pickerSlot.dayIdx) return day;
        return { ...day, slots: { ...day.slots, [pickerSlot.meta.id]: makeSlot(drillId) } };
      }),
    );
    setMissedSlots((prev) => prev.filter((s) => s !== pickerSlot.meta.id));
    setPickerSlot(null);
  };

  const updateSlotField = (dayIdx: number, slotId: string, field: keyof Omit<DrillPlanSlot, 'drillId'>, value: string) => {
    setDays((prev) =>
      prev.map((day, di) => {
        if (di !== dayIdx) return day;
        const existing = day.slots[slotId] ?? makeSlot('');
        return { ...day, slots: { ...day.slots, [slotId]: { ...existing, [field]: value } } };
      }),
    );
  };

  const handleSave = async () => {
    if (!client || !template || days.length === 0 || !user?.uid) {
      toast({ title: 'Select a template and ensure drills are filled.', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const phase = filters?.phaseCode ?? clientPhase ?? 'P1';
      const tags = buildDrillPlanTags(phase, client.liftingExperience, deriveClientPain(client).painStatus);
      const slotLabels: Record<string, string> = {};
      for (const s of template.slots) slotLabels[s.id] = s.label;

      const planId = await createDrillPlan({
        name: planName || `${clientDisplayLabel(client)} – ${template.name} Plan`,
        templateId: template.id,
        phase,
        twiceDaily: template.twiceDaily,
        tags,
        days,
        slotLabels,
        suggestionFilterSnapshot: filters ?? undefined,
        createdBy: user.uid,
      });

      await createDrillPlanAssignment(client.id, {
        drillPlanDefinitionId: planId,
        clientId: client.id,
        assigningPractitionerUid: user.uid,
        drillPlanName: planName || `${clientDisplayLabel(client)} – ${template.name} Plan`,
      });

      logger.info('drill-plan-assignment:create', { planId, clientId: client.id, assignedByUid: user.uid });
      toast({ title: 'Plan saved!', description: `Assigned to ${clientDisplayLabel(client)}.` });
      onSaved?.(planId);

      // Reset
      setStep('template');
      setTemplate(null);
      setDays([]);
      setFilters(null);
      setPlanName('');
      setMissedSlots([]);
      setSlotSuggestions({});
    } catch (err) {
      logger.error('drill-plan:save:error', err);
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const scrollMaxHeight = 'max-h-[calc(100dvh-10.5rem)]';

  return (
    <ClientRequiredGate>
      <div className="flex flex-col min-h-0 -mx-1 sm:mx-0">
        <StepBar step={step} clientName={client ? clientDisplayLabel(client) : undefined} clientPhase={clientPhase} />

        {/* ── STEP: template — content height only (no flex-1 stretch) ─ */}
        {step === 'template' && (
          <div className="px-2 pt-1.5 pb-3 space-y-2">
            <p className="text-xs text-muted leading-snug">
              Choose a template — drills auto-fill from{' '}
              {client ? clientDisplayLabel(client) : 'the client'}&apos;s phase, posture &amp; assessment.
            </p>

            <div className="space-y-2">
              {TEMPLATE_DEFS.map(({ template: tpl, colour }) => {
                const phaseOk = !clientPhase || tpl.allowedPhases.includes(clientPhase);
                return (
                  <button
                    key={tpl.id}
                    disabled={!phaseOk || isAutoFilling}
                    onClick={() => handleSelectTemplate(tpl)}
                    className={cn(
                      'w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all active:scale-[0.98]',
                      phaseOk
                        ? `${colour} hover:border-opacity-70`
                        : 'opacity-40 cursor-not-allowed border-line',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{tpl.name}</div>
                        <div className="text-xs opacity-80 mt-0.5">{tpl.description}</div>
                        {tpl.preferredDomain && (
                          <div className="text-xs mt-1 font-medium opacity-90">Positional drills only</div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <div className="flex gap-1 flex-wrap justify-end">
                          {tpl.allowedPhases.map((p) => (
                            <span key={p} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-fg/20">{p}</span>
                          ))}
                          {tpl.twiceDaily && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-fg/20">2×/day</span>
                          )}
                        </div>
                        <span className="text-xs opacity-70">
                          {tpl.slots.length} drills · {tpl.twiceDaily ? '2×/day · ' : ''}
                          {tpl.dayNames.length} days
                        </span>
                      </div>
                    </div>
                    {!phaseOk && (
                      <p className="text-xs mt-1.5 text-muted">
                        Requires {tpl.allowedPhases.join(' or ')} — client is {clientPhase}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            {isAutoFilling && (
              <div className="flex items-center gap-2 text-muted py-2 justify-center text-sm">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                <span>Filling drills…</span>
              </div>
            )}
          </div>
        )}

        {/* ── STEP: drills ──────────────────────────────────────────── */}
        {step === 'drills' && template && (
          <>
          <div className={cn('overflow-y-auto px-2 py-2 space-y-2', scrollMaxHeight)}>
            {/* Header row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setStep('template'); setTemplate(null); setDays([]); }}
                  className="p-1.5 -ml-1.5 rounded-lg text-muted hover:text-fg hover:bg-accent"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold">{template.name}</span>
                {template.twiceDaily && <Badge variant="outline" className="text-xs">2×/day</Badge>}
                {filters?.phaseTag && <Badge variant="secondary" className="text-xs">{filters.phaseTag}</Badge>}
              </div>
            </div>

            {/* Dual suggestion toggle */}
            {hasDualSuggestions && (
              <div className="rounded-xl border bg-fg/5 p-3 space-y-2">
                <p className="text-xs text-muted font-medium">Swap all drills to:</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => applyMode('posture')}
                    className={cn(
                      'flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-medium transition-colors',
                      suggestionMode === 'posture'
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-line text-muted hover:bg-accent',
                    )}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Posture-based
                  </button>
                  <button
                    onClick={() => applyMode('ai')}
                    className={cn(
                      'flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-medium transition-colors',
                      suggestionMode === 'ai'
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-line text-muted hover:bg-accent',
                    )}
                  >
                    <Brain className="h-3.5 w-3.5" />
                    Worst measurement
                  </button>
                </div>
              </div>
            )}

            {/* Slot list — only Day 1 shown (all days have same drills at this stage) */}
            <div className="space-y-2">
              <p className="text-xs text-muted uppercase tracking-wide font-semibold">
                Drills — tap to swap
              </p>
              {template.slots.map((slotMeta, idx) => {
                const slot = days[0]?.slots[slotMeta.id];
                const drill = slot ? drillById[slot.drillId] : undefined;
                const isMissed = !slot && missedSlots.includes(slotMeta.id);

                return (
                  <button
                    key={slotMeta.id}
                    onClick={() => {
                      setPickerSlot({ meta: slotMeta, dayIdx: 0 });
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-xl px-3.5 py-3 text-left border-2 transition-colors active:scale-[0.99]',
                      drill
                        ? 'border-line hover:border-accent/40 bg-surface'
                        : isMissed
                        ? 'border-dashed border-status-warn/50 bg-status-warn-surface/50'
                        : 'border-dashed border-muted-foreground/30',
                    )}
                  >
                    {/* Number bubble */}
                    <span className="w-6 h-6 rounded-full bg-fg/10 flex items-center justify-center text-[10px] font-bold text-muted flex-shrink-0">
                      {idx + 1}
                    </span>

                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-medium text-muted">{slotMeta.label}</div>
                      {drill ? (
                        <div className="text-sm font-semibold truncate">{drill.name}</div>
                      ) : (
                        <div className="text-xs text-muted italic flex items-center gap-1">
                          <Wand2 className="h-3 w-3" />
                          {isMissed ? 'No match — tap to pick' : 'Tap to add drill'}
                        </div>
                      )}
                    </div>

                    <ChevronRight className="h-4 w-4 text-muted flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>

          {template && !isAutoFilling && days.length > 0 && (
            <div className="flex-shrink-0 px-2 py-2 border-t bg-bg">
              <Button
                className="w-full h-10 gap-2 text-sm"
                onClick={() => setStep('details')}
              >
                Next — Set reps & details
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          </>
        )}

        {/* ── STEP: details ─────────────────────────────────────────── */}
        {step === 'details' && template && (
          <>
          <div className={cn('overflow-y-auto px-2 py-2 space-y-2', scrollMaxHeight)}>
            <button
              onClick={() => setStep('drills')}
              className="flex items-center gap-1.5 text-sm text-muted hover:text-fg -ml-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to drills
            </button>

            {/* Plan name */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted uppercase tracking-wide">
                Plan name
              </label>
              <Input
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder={`${client ? clientDisplayLabel(client) : 'Client'} – ${template.name} Plan`}
                className="h-11 text-sm"
              />
            </div>

            {/* Day tabs */}
            {days.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {days.map((day, di) => (
                  <button
                    key={di}
                    onClick={() => setActiveDay(di)}
                    className={cn(
                      'flex-shrink-0 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      activeDay === di
                        ? 'bg-accent text-accent-fg'
                        : 'bg-fg/10 text-muted hover:bg-accent',
                    )}
                  >
                    {day.name}
                  </button>
                ))}
              </div>
            )}

            {/* Slot detail rows */}
            <div className="space-y-3">
              {template.slots.map((slotMeta, idx) => {
                const slot = days[activeDay]?.slots[slotMeta.id];
                const drill = slot ? drillById[slot.drillId] : undefined;

                return (
                  <div key={slotMeta.id} className="rounded-xl border bg-surface px-4 py-3 space-y-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-fg/10 flex items-center justify-center text-[10px] font-bold text-muted flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-muted">{slotMeta.label}</div>
                        {drill ? (
                          <div className="text-sm font-semibold truncate">{drill.name}</div>
                        ) : (
                          <div className="text-xs text-muted italic">No drill selected</div>
                        )}
                      </div>
                    </div>

                    {/* Sets / Reps / Rest row */}
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { field: 'sets' as const, label: 'Sets' },
                        { field: 'reps' as const, label: 'Reps' },
                        { field: 'rest' as const, label: 'Rest' },
                      ].map(({ field, label }) => (
                        <div key={field} className="space-y-0.5">
                          <label className="text-[10px] text-muted block text-center">
                            {label}
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={(slot as any)?.[field] ?? ''}
                            onChange={(e) => updateSlotField(activeDay, slotMeta.id, field, e.target.value)}
                            placeholder="—"
                            className="w-full h-10 rounded-lg border border-line bg-bg text-center text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex-shrink-0 px-2 py-2 border-t bg-bg space-y-2">
            <Button
              className="w-full h-10 gap-2 text-sm"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSaving ? 'Saving…' : 'Save & Assign to Client'}
            </Button>
            <Button
              variant="outline"
              className="w-full h-10 gap-2 text-sm"
              onClick={() => {
                if (!template || days.length === 0) return;
                const phase = filters?.phaseCode ?? clientPhase ?? 'P1';
                const slotLabels: Record<string, string> = {};
                for (const s of template.slots) slotLabels[s.id] = s.label;
                printDrillPlan(
                  {
                    name: planName || `${client ? clientDisplayLabel(client) : 'Client'} – ${template.name} Plan`,
                    templateId: template.id,
                    phase,
                    twiceDaily: template.twiceDaily,
                    tags: buildDrillPlanTags(phase, client?.liftingExperience, deriveClientPain(client).painStatus),
                    days,
                    slotLabels,
                    createdBy: user?.uid ?? '',
                  },
                  drillById,
                  client ? clientDisplayLabel(client) : undefined,
                );
              }}
              disabled={days.length === 0}
            >
              <FileDown className="h-4 w-4" />
              Export PDF
            </Button>
          </div>
          </>
        )}

        {/* ── Drill Library picker (full library UI in a bottom sheet) ─────── */}
        <DrillLibraryPickerSheet
          isOpen={!!pickerSlot}
          onClose={() => setPickerSlot(null)}
          slotLabel={pickerSlot?.meta.label ?? ''}
          onSelect={handleSlotDrillSelect}
        />
      </div>
    </ClientRequiredGate>
  );
};

// ─── Step progress indicator ─────────────────────────────────────────────────

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'template', label: 'Template' },
  { key: 'drills', label: 'Drills' },
  { key: 'details', label: 'Details' },
];

const StepBar: React.FC<{
  step: WizardStep;
  clientName?: string;
  clientPhase?: 'P1' | 'P2' | 'P3' | null;
}> = ({ step, clientName, clientPhase }) => {
  const current = STEPS.findIndex((s) => s.key === step);
  return (
    <div className="flex-shrink-0 border-b bg-bg px-2 py-1.5 space-y-1">
      {clientName && (
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold truncate">{clientName}</span>
          {clientPhase && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 shrink-0">
              {clientPhase}
            </Badge>
          )}
        </div>
      )}
      <div className="flex items-center gap-1">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="flex items-center gap-1 shrink-0">
              <div
                className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                  i < current
                    ? 'bg-accent text-accent-fg'
                    : i === current
                    ? 'bg-accent text-accent-fg'
                    : 'bg-fg/10 text-muted',
                )}
              >
                {i < current ? '✓' : i + 1}
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium',
                  i === current ? 'text-accent' : 'text-muted',
                )}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-px min-w-[0.5rem] transition-colors',
                  i < current ? 'bg-accent' : 'bg-fg/10',
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default MobileDrillPlanWizard;
